import { useMutation } from '/@/core/graphql'
import { gql } from '@apollo/client/core'
import {
  type Project,
  type ProjectThingsInput,
  type ProjectThingInput,
} from '../types'
import { projectThingQueryFragment } from './fragments'
import { addListItemToCache, deleteListItemFromCache } from '/@/utils/apollo'
import {
  type LayoutFormat,
  type ProjectThing,
  type ThingInput,
  type ProjectThingInput as CoreProjectThingInput,
} from '/@/common/things/types'

interface AddProjectThingsInput {
  projectId: string
  sectionId?: string | null
  things?: CoreProjectThingInput[]
}

interface MoveToProjectInput {
  things?: ThingInput[]
  imageUrls?: string[]
  projectThings: ProjectThingInput[]
  origProjectId: string
  destProjectId: string
}

interface ProjectThingLayoutFormatInput {
  projectId: string
  projectThing: ProjectThingInput
  layoutFormat: LayoutFormat
}

export function useProjectThingApi() {
  const { mutate: addToProject } = useMutation(gql`
    ${projectThingQueryFragment}
    mutation addThingToProject($input: AddProjectThingsInput!) {
      project {
        thing {
          add(input: $input) {
            recordIds
            status
            project {
              id
            }
            records {
              ...ProjectThingProps
            }
          }
        }
      }
    }
  `)

  const { mutate: removeFromProjectMut } = useMutation(gql`
    mutation removeThingFromProject($input: ProjectThingsInput!) {
      project {
        thing {
          remove(input: $input) {
            recordIds
            status
            project {
              id
            }
          }
        }
      }
    }
  `)

  const { mutate: updateLayoutFormat } = useMutation(gql`
    mutation updateProjectThingLayoutFormat(
      $input: ProjectThingLayoutFormatInput!
    ) {
      project {
        thing {
          updateLayoutFormat(input: $input) {
            recordIds
            status
            records {
              id
              layoutFormat
            }
          }
        }
      }
    }
  `)

  async function removeFromProject(input: ProjectThingsInput) {
    await removeFromProjectMut(
      { input },
      {
        update(
          cache,
          {
            data: {
              project: {
                thing: { remove: data },
              },
            },
          },
        ) {
          for (const id of data.recordIds) {
            deleteListItemFromCache(
              cache.identify(data.project),
              cache,
              'things',
              id,
            )
          }
        },
      },
    )
  }

  async function addToProjectWithRecords(
    input: AddProjectThingsInput,
  ): Promise<ProjectThing[] | undefined> {
    const response = await addToProject(
      { input },
      {
        update(
          cache,
          {
            data: {
              project: {
                thing: { add: data },
              },
            },
          },
        ) {
          for (const record of data.records) {
            const newItem = cache.writeFragment({
              data: record,
              fragmentName: 'ProjectThingProps',
              fragment: projectThingQueryFragment,
            })
            if (newItem) {
              addListItemToCache<Project>(
                cache.identify(data.project),
                cache,
                'things',
                newItem,
                {
                  insertMode: 'prepend',
                },
              )
            }
          }
        },
      },
    )
    return response?.data.project.thing.add.records
  }

  return {
    addToProjectWithRecords,
    async addToProject(
      input: AddProjectThingsInput,
    ): Promise<string[] | undefined> {
      const records = await addToProjectWithRecords(input)
      return records?.map(r => r.id)
    },
    removeFromProject,
    async moveToProject(input: MoveToProjectInput) {
      const addInput = {
        things: input.things,
        projectId: input.destProjectId,
      }
      const removeInput = {
        projectThings: input.projectThings,
        projectId: input.origProjectId,
      }
      await removeFromProject(removeInput)
      await addToProject({ input: addInput })
    },
    async updateLayoutFormat(input: ProjectThingLayoutFormatInput) {
      await updateLayoutFormat({ input })
    },
  }
}
